import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Text } from '@react-three/drei';
import * as THREE from 'three';
import { useXRInputSourceStateContext, useXRInputSourceEvent, DefaultXRController, DefaultXRHand } from '@react-three/xr';
import { shared } from './shared';
import { ZONES } from '../config/zones';
import { useStore } from '../store';

const R = 0.075; // minimap radius (metres)

// Small analog joystick that floats over the LEFT hand. Thumbstick or pinch/trigger drives movement.
function LeftJoystick({ state }) {
  const knob = useRef();
  const pinch = useRef(false);
  useXRInputSourceEvent(state?.inputSource, 'selectstart', () => { pinch.current = true; }, []);
  useXRInputSourceEvent(state?.inputSource, 'selectend', () => { pinch.current = false; }, []);

  useFrame(() => {
    let x = 0, y = 0;
    const g = state?.gamepad;
    if (g) {
      const ts = g['xr-standard-thumbstick'] || g['xr-standard-touchpad'];
      if (ts) { x = ts.xAxis || 0; y = ts.yAxis || 0; }
    }
    if (Math.abs(x) > 0.08 || Math.abs(y) > 0.08) {
      shared.input.turn = -x;
      shared.input.forward = -y;
    } else if (pinch.current) {
      shared.input.forward = 0.7;
      shared.input.turn = 0;
    } else {
      shared.input.forward = 0;
      shared.input.turn = 0;
    }
    if (knob.current) knob.current.position.set(x * 0.03, 0.006, y * 0.03);
  });

  return (
    <group position={[0, 0.06, -0.03]} rotation={[-Math.PI / 2.2, 0, 0]}>
      <mesh>
        <cylinderGeometry args={[R, R, 0.008, 24]} />
        <meshStandardMaterial color="#0a0a0a" transparent opacity={0.75} emissive="#D4AF37" emissiveIntensity={0.12} />
      </mesh>
      <mesh ref={knob} position={[0, 0.006, 0]}>
        <sphereGeometry args={[0.02, 12, 10]} />
        <meshStandardMaterial color="#D4AF37" emissive="#D4AF37" emissiveIntensity={0.8} toneMapped={false} />
      </mesh>
    </group>
  );
}

// A live minimap that floats over the RIGHT hand (Navy/Red/Sky-Blue). Thumbstick Y controls altitude.
function RightMinimap({ state }) {
  const dot = useRef();
  const snapArmed = useRef(true);
  useFrame(() => {
    const g = state?.gamepad;
    if (g) {
      const ts = g['xr-standard-thumbstick'] || g['xr-standard-touchpad'];
      if (ts) {
        const x = ts.xAxis || 0;
        shared.input.up = -(ts.yAxis || 0);
        // Comfort snap-turn: flick the right stick left/right to rotate 30°
        if (Math.abs(x) > 0.7 && snapArmed.current) {
          shared.snapYaw += (x > 0 ? -1 : 1) * (Math.PI / 6);
          snapArmed.current = false;
        } else if (Math.abs(x) < 0.3) {
          snapArmed.current = true;
        }
      }
    }
    if (dot.current) {
      const x = THREE.MathUtils.clamp(shared.camPos.x / 210, -1, 1) * R;
      const z = THREE.MathUtils.clamp(shared.camPos.z / 125, -1, 1) * R;
      dot.current.position.set(x, 0.008, z);
    }
  });

  return (
    <group position={[0, 0.06, -0.03]} rotation={[-Math.PI / 2.2, 0, 0]}>
      {/* Liberal (navy) top half */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[R, 24, 0, Math.PI]} />
        <meshBasicMaterial color="#1e3a8a" transparent opacity={0.85} side={THREE.DoubleSide} />
      </mesh>
      {/* Conservative (red) bottom half */}
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[R, 24, Math.PI, Math.PI]} />
        <meshBasicMaterial color="#7f1d1d" transparent opacity={0.85} side={THREE.DoubleSide} />
      </mesh>
      {/* River band (sky blue) */}
      <mesh position={[0, 0.002, 0]}>
        <boxGeometry args={[R * 2, 0.002, R * 0.18]} />
        <meshBasicMaterial color="#38bdf8" />
      </mesh>
      {/* Zone dots */}
      {ZONES.map((zn) => (
        <mesh key={zn.id} position={[(zn.x / 210) * R, 0.004, 0]}>
          <sphereGeometry args={[0.004, 6, 6]} />
          <meshBasicMaterial color="#D4AF37" />
        </mesh>
      ))}
      {/* Player dot (high contrast) */}
      <mesh ref={dot} position={[0, 0.008, 0]}>
        <sphereGeometry args={[0.007, 8, 8]} />
        <meshStandardMaterial color="#ffffff" emissive="#D4AF37" emissiveIntensity={1.2} toneMapped={false} />
      </mesh>
    </group>
  );
}

// A floating "Ecology Deck" that rides above the LEFT hand. It shows the current
// creature's name + ideological symbolism (from the Field Guide) in large,
// high-contrast text so VR users can read the research as they explore.
const IDLE_TITLE = 'FIELD RESEARCH';
const IDLE_BODY = 'Fly close to any creature — the Harpy Eagle will identify it and its meaning here.';

function EcologyDeck() {
  const grp = useRef();
  const nameRef = useRef();
  const bodyRef = useRef();
  const lastName = useRef('__init__');
  const pulse = useRef(0);

  useFrame((state, delta) => {
    if (!grp.current) return;
    const card = useStore.getState().fieldCard;
    const active = !!(card && card.name);
    const name = active ? card.name.toUpperCase() : IDLE_TITLE;

    if (name !== lastName.current) {
      lastName.current = name;
      pulse.current = active ? 1 : 0;
      if (nameRef.current) nameRef.current.text = name;
      if (bodyRef.current) bodyRef.current.text = active ? card.text : IDLE_BODY;
    }

    // Gentle attention pulse (scale + gold border glow) when a new creature is identified.
    if (pulse.current > 0) pulse.current = Math.max(0, pulse.current - delta * 1.1);
    const s = 1 + pulse.current * 0.06;
    grp.current.scale.setScalar(s);
  });

  return (
    <group ref={grp} position={[0, 0.16, -0.05]} rotation={[-0.6, 0, 0]}>
      {/* Backing slate */}
      <mesh position={[0, 0, -0.001]}>
        <planeGeometry args={[0.28, 0.2]} />
        <meshBasicMaterial color="#04070d" transparent opacity={0.88} side={THREE.DoubleSide} />
      </mesh>
      {/* Gold frame for high contrast */}
      <lineSegments>
        <edgesGeometry args={[new THREE.PlaneGeometry(0.28, 0.2)]} />
        <lineBasicMaterial color="#D4AF37" toneMapped={false} />
      </lineSegments>
      {/* Header */}
      <Text position={[0, 0.078, 0.001]} anchorX="center" anchorY="middle" fontSize={0.014} letterSpacing={0.14} color="#D4AF37" outlineWidth={0.0006} outlineColor="#000">
        ECOLOGY DECK
      </Text>
      {/* Creature name (large, gold) */}
      <Text ref={nameRef} position={[0, 0.045, 0.001]} anchorX="center" anchorY="middle" fontSize={0.024} color="#ffe58a" outlineWidth={0.0012} outlineColor="#000" maxWidth={0.26}>
        {IDLE_TITLE}
      </Text>
      {/* Symbolism body (high-contrast white) */}
      <Text ref={bodyRef} position={[0, -0.028, 0.001]} anchorX="center" anchorY="middle" fontSize={0.0135} color="#ffffff" outlineWidth={0.0008} outlineColor="#000" maxWidth={0.25} lineHeight={1.3}>
        {IDLE_BODY}
      </Text>
    </group>
  );
}

// One component used for both hands/controllers; branches on handedness.
export default function HandDeck() {
  const state = useXRInputSourceStateContext();
  const handed = state?.inputSource?.handedness;
  const isHand = !!state?.inputSource?.hand;
  return (
    <>
      {isHand ? <DefaultXRHand /> : <DefaultXRController />}
      {handed === 'right' ? (
        <RightMinimap state={state} />
      ) : (
        <>
          <LeftJoystick state={state} />
          <EcologyDeck />
        </>
      )}
    </>
  );
}
